<template>
  <Modal
    class="pricing-report-modal-root"
    :visible="modalVisible"
    :title="'Report pricing bug'"
    @close="onModalClose"
    no-close-btn
    do-not-stop-propagation
  >
    <div class="pricing-report-form-root">
      <div class="pricing-report-input-root">
        <div class="pricing-report-input-label">
          Problem description
        </div>
        <textarea
          ref="textareaElement"
          class="pricing-report-input pricing-report-input--description"
          rows="4"
          v-model="reportDescription"
        />
      </div>
      <div class="pricing-report-input-root pricing-report-input-root--time-range">
        <div class="pricing-report-input-label">
          Time range
        </div>
        <Dropdown
          right
          :label="selectedTimeRangeOption?.label || 'Select time range'"
        >
          <DropdownItem
            v-for="option in timeRangeOptions"
            :key="option.value"
            @click="selectTimeRangeOption(option)"
            clickable
          >
            {{ option.label }}
          </DropdownItem>
        </Dropdown>
      </div>
      <DateTimePicker
        v-if="selectedTimeRangeOption?.value === 'date-range'"
        @onDateTimeUpdate="onDateTimeRangeUpdate"
      />
      <div class="pricing-report-input-root">
        <div class="pricing-report-input-label">
          Markets affected
        </div>
        <Dropdown
          right
          :label="marketsSelectDropdownLabel"
          class="market-search-dropdown"
        >
          <div class="market-search-input">
            <TextInput
              class="market-search-field"
              :model-value="marketSearchValue"
              @update:modelValue="updateMarketSearchValue"
              icon="search"
              placeholder="Search"
              small
            />
          </div>
          <DropdownItem
            v-for="option in pricingReportMarketList"
            :key="option.value"
            @click="toggleIsMarketSelected(option)"
            clickable
            omit-menu-close
          >
            <Checkbox
              :model-value="option.isSelected"
              @update:modelValue="toggleIsMarketSelected(option)"
            />
            <span>{{ option.displayName }}</span>
          </DropdownItem>
          <div
            v-if="noMarketsDataText"
            class="no-markets-data-text"
          >
            {{ noMarketsDataText }}
          </div>
        </Dropdown>
      </div>
    </div>
    <div class="pricing-report-footer-root">
      <Button
        variant="tertiary"
        @click="onModalClose"
      >
        Dismiss
      </Button>
      <Button
        variant="primary"
        :disabled="reportDataInvalid"
        @click="submitReport"
      >
        Send
      </Button>
    </div>
  </Modal>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { format, subMinutes } from 'date-fns';
import {
  filter, flatMap, includes, isNil,
  map, orderBy, toLower, find,
  findIndex, get,
} from 'lodash';
import Modal from '@/components/common/Modal';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';
import DateTimePicker from '@/components/common/DateTimePicker';
import TextInput from '@/components/common/TextInput';
import Checkbox from '@/components/common/Checkbox';
import Button from '@/components/common/Button';
import MarketMapper from '@/services/helpers/market-mapper';

export default {
  emits: ['onModalClose'],
  components: {
    Modal,
    Dropdown,
    DropdownItem,
    DateTimePicker,
    TextInput,
    Checkbox,
    Button,
  },
  props: {
    modalVisible: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    marketsObject: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const reportDescription = ref('');
    const timeRangeOptions = ref([
      {
        value: 'last-minute',
        label: 'Last minute',
      },
      {
        value: 'last-5-minutes',
        label: 'Last 5 minutes',
      },
      {
        value: 'last-10-minutes',
        label: 'Last 10 minutes',
      },
      {
        value: 'date-range',
        label: 'Date range',
      },
    ]);
    const selectedTimeRangeOption = ref(null);
    const selectedDateTimeRange = ref({
      from: null,
      to: null,
      fieldMissing: false,
      timeError: false,
    });
    const selectTimeRangeOption = (option) => {
      selectedTimeRangeOption.value = option;
      selectedDateTimeRange.value = {
        from: null,
        to: null,
        fieldMissing: false,
        timeError: false,
      };
    };
    const extractTimeRangeValue = () => {
      const option = selectedTimeRangeOption.value;
      if (option.value === 'date-range') {
        return {
          from: selectedDateTimeRange.value.from,
          to: selectedDateTimeRange.value.until,
        };
      }
      let minutesToSubtract;
      if (option.value === 'last-minute') {
        minutesToSubtract = 1;
      } else if (option.value === 'last-5-minutes') {
        minutesToSubtract = 5;
      } else if (option.value === 'last-10-minutes') {
        minutesToSubtract = 10;
      }
      return {
        from: format(subMinutes(new Date(), minutesToSubtract), "yyyy-MM-dd'T'HH:mm:ss"),
        to: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
      };
    };
    const onDateTimeRangeUpdate = (dateTimeRange) => {
      selectedDateTimeRange.value = dateTimeRange;
    };
    const marketSearchValue = ref('');
    const updateMarketSearchValue = (value) => {
      marketSearchValue.value = value;
    };
    // Pricing report markets
    const homeTeam = computed(() => props.event?.competitors?.[0]);
    const awayTeam = computed(() => props.event?.competitors?.[1]);
    const eventMarkets = computed(() => get(store.getters, props.marketsObject));
    const selectedPricingReportMarkets = ref([]);
    const marketsSelectDropdownLabel = computed(() => {
      if (!selectedPricingReportMarkets.value.length) return 'Select one or multiple markets';
      return `${selectedPricingReportMarkets.value.length} markets selected`;
    });

    const marketsDisplayConfiguration = computed(() => orderBy(filter(store.getters.displayMarketsConfiguration,
      (market) => market.active), 'position'));

    const filteredMarkets = computed(() => filter(eventMarkets.value,
      (market) => (includes(toLower(market.marketCode), marketSearchValue.value)
        || includes(toLower(market.marketSummary?.playerName), marketSearchValue.value)
        || (!isNil(market.marketType.params.TEAM) && includes(toLower(homeTeam.value?.name || ''), marketSearchValue.value))
        || (!isNil(market.marketType.params.TEAM) && includes(toLower(awayTeam.value?.name || ''), marketSearchValue.value))
        || includes(toLower(find(marketsDisplayConfiguration.value,
          (displayMarket) => displayMarket.marketCode === market.marketCode
            && displayMarket.selections === market.marketType.params.SELECTIONS)?.defaultName), marketSearchValue.value))));

    const marketDisplayConfigurations = computed(() => filter(map(marketsDisplayConfiguration.value, (marketConfiguration) => ({
      ...marketConfiguration,
      markets: map(MarketMapper.sortSectionMarkets(filter(filteredMarkets.value, (market) => market.marketCode === marketConfiguration.marketCode
      && market.marketType.params.SELECTIONS === marketConfiguration.selections), props.event?.isUsaView), (filterMarket) => {
        const mappedMarket = MarketMapper.mapUngroupedMarket({
          marketDisplayConfiguration: marketConfiguration,
          market: filterMarket,
          homeTeam: homeTeam.value,
          awayTeam: awayTeam.value,
        });
        return {
          ...mappedMarket,
          selections: MarketMapper.sortSelections({ selections: filterMarket.selections, isUSAView: props.event?.isUsaView }),
          isSelected: includes(selectedPricingReportMarkets.value, mappedMarket.marketId),
        };
      }),
    })), (marketDisplayConfiguration) => marketDisplayConfiguration.markets?.length));

    const pricingReportMarketList = computed(() => flatMap(marketDisplayConfigurations.value, (marketConf) => marketConf.markets));
    const noMarketsDataText = computed(() => {
      if (!eventMarkets.value.length && !marketSearchValue.value) return 'No available markets';
      if (marketSearchValue.value) return 'No markets found';
      return '';
    });

    const toggleIsMarketSelected = (market) => {
      const marketIndex = findIndex(selectedPricingReportMarkets.value, (selectedMarket) => selectedMarket === market.marketId);
      if (marketIndex === -1) {
        selectedPricingReportMarkets.value.push(market.marketId);
      } else {
        selectedPricingReportMarkets.value = filter(selectedPricingReportMarkets.value, (selectedMarket) => selectedMarket !== market.marketId);
      }
    };

    const reportDataInvalid = computed(() => {
      if (!reportDescription.value
      || !selectedTimeRangeOption.value
      || !selectedPricingReportMarkets.value.length) return true;
      if (selectedTimeRangeOption.value?.value === 'date-range') {
        return !selectedDateTimeRange.value.from
        || !selectedDateTimeRange.value.until
        || selectedDateTimeRange.value.fieldMissing
        || selectedDateTimeRange.value.timeError;
      }
      return false;
    });

    const clearReportData = () => {
      reportDescription.value = '';
      selectedTimeRangeOption.value = null;
      selectedDateTimeRange.value = {
        from: null,
        to: null,
        fieldMissing: false,
        timeError: false,
      };
      marketSearchValue.value = '';
      selectedPricingReportMarkets.value = [];
    };

    const onModalClose = () => {
      emit('onModalClose');
      clearReportData();
    };

    const submitReport = () => {
      const { from, to } = extractTimeRangeValue();
      const reportData = {
        description: reportDescription.value,
        from,
        to,
        marketIds: selectedPricingReportMarkets.value,
        eventId: props.event.eventId,
      };
      store.dispatch('reportPricingBug', reportData).then(() => {
        onModalClose();
      });
    };
    return {
      reportDescription,
      timeRangeOptions,
      selectedTimeRangeOption,
      marketSearchValue,
      reportDataInvalid,
      pricingReportMarketList,
      marketsSelectDropdownLabel,
      updateMarketSearchValue,
      onModalClose,
      selectTimeRangeOption,
      toggleIsMarketSelected,
      submitReport,
      onDateTimeRangeUpdate,
      noMarketsDataText,
    };
  },
};
</script>

<style lang="scss">
.pricing-report-modal-root {
  .modal__dialog {
    min-width: 480px;
    .modal__header {
      height: auto;
      padding: 16px;
      border-bottom: 1px solid $gray400;
      .modal__title {
        padding: 0;
      }
    }
    .modal__content {
      .pricing-report-form-root {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 16px;
        .pricing-report-input-root {
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          z-index: 1;
          &--time-range {
            z-index: 2;
          }
          .pricing-report-input-label {
            font-size: 10px;
            font-weight: 600;
            color: $gray800;
            text-transform: uppercase;
          }
          .pricing-report-input {
            width: 100%;
            &:focus {
              outline: none;
            }
            &--description {
              max-height: 80px;
              padding: 8px;
              border: 1px solid $gray400;
              border-radius: 4px;
              resize: none;
            }
          }
          .market-search-dropdown {
            position: relative;
            .market-search-input {
              padding: 4px;
              position: sticky;
              top: 0;
              background-color: $white;
              .market-search-field {
                width: 100%;
              }
            }
            .dropdown-menu {
              max-height: 200px;
              width: 100%;
              overflow-y: auto;
              overflow-x: hidden;
              .dropdown-item {
                width: 100%;
                display: flex;
                align-items: center;
                padding: 8px;
                gap: 8px;
                span {
                  width: 100%;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }
              }
            }
            .no-markets-data-text {
              padding: 8px;
              font-size: 12px;
              color: $gray800;
              text-align: center;
            }
          }
        }
      }
      .pricing-report-footer-root {
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 4px;
        border-top: 1px solid $gray400;
      }
    }
  }
}
</style>
